import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaLinkedin, FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";

const translations = {
  en: {
    title: "Share on LinkedIn",
    description: "Share your job posting on LinkedIn to reach professional candidates",
    share_button: "Share on LinkedIn",
    copy_link: "Copy link",
    link_copied: "Link copied to clipboard",
    enable_channel: "Enable Channel",
    disable_channel: "Disable Channel",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    back: "Back to channels",
    share_url: "Share URL",
    preview: "Preview",
    instructions: "Click the button below to share this job posting on LinkedIn. A sharing dialog will open with the job link pre-filled.",
  },
  fr: {
    title: "Partager sur LinkedIn",
    description: "Partagez votre offre d'emploi sur LinkedIn pour atteindre des candidats professionnels",
    share_button: "Partager sur LinkedIn",
    copy_link: "Copier le lien",
    link_copied: "Lien copié dans le presse-papiers",
    enable_channel: "Activer le canal",
    disable_channel: "Désactiver le canal",
    status: "Statut",
    active: "Actif",
    inactive: "Inactif",
    back: "Retour aux canaux",
    share_url: "URL de partage",
    preview: "Aperçu",
    instructions:
      "Cliquez sur le bouton ci-dessous pour partager cette offre d'emploi sur LinkedIn. Une boîte de dialogue de partage s'ouvrira avec le lien de l'offre pré-rempli.",
  },
};

export default function LinkedInShareChannel({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelSlug = "share-on-linkedin";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      setLoading(true);
      const channelResponse = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });
      if (channelResponse.ok && channelResponse.data?.[0]) {
        setChannel(channelResponse.data[0]);
      }
    } catch (e) {
      console.error("Error loading channel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `https://jobego.co/companies/${job.organization_slug}/jobs/${jobPost._id}`;
  }, [job, jobPost]);

  const handleShare = () => {
    if (!shareLink) return;
    const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`;
    window.open(linkedInShareUrl, "_blank", "width=600,height=600");
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          {channel?.logo ? (
            <img src={channel.logo} alt={channel.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <FaLinkedin className="w-8 h-8 text-blue-700" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>

            <p className="text-sm text-gray-600 mb-6">{channel?.description || t("description")}</p>

            {jobPost && (
              <div className="flex items-center gap-2">
                <button onClick={handleShare} className="btn btn-primary flex items-center gap-2 bg-blue-700 hover:bg-blue-800">
                  <FaLinkedin className="w-4 h-4" />
                  {t("share_button")}
                  <FaExternalLinkAlt className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
